import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { getRandomNumbersNoDuplicates } from '../../../Utils/NumberUtils';
import { areArraysEqual } from '../../../Utils/StringUtils';
import KeyboardChar from './KeyboardChar';
import LetterRow from './LetterRow';
import PinCodeGuess from './PinCodeGuess';
import GameHeader from '../../Layout/Public/Apps/NumberRecognitionGame/GameHeader';
import HowToPlayModal from '../../Layout/Public/Apps/CountdownTimer/HowToPlayModal';
import { addArrayToArrayInLocalStorage } from '../../../Utils/LocalStorageUtils';
import { PINCODE_GUESSES } from '../../../constants';

interface PinCodeGameProps {}

export type NumberLetterObj = {
  correctPosition: number;
  letter: string;
  number: string;
};

const INITIAL_PINCODE_VALUES = ['', '', '', ''];

const ROW_LENGTH = 5;

const COLUMN_WIDTH = 3;

const DEFAULT_NUMBER_LETTER = {
  correctPosition: -1,
  letter: '-',
  number: '-1',
};

const PinCodeGame: FC<PinCodeGameProps> = () => {
  const [guesses, setGuesses] = useState<any>([]);
  // const [guesses, setGuesses] = useState<any>([
  //   ['A', 'B', 'C', 'D'],
  //   ['A', 'B', 'C', 'D'],
  //   ['A', 'B', 'C', 'D'],
  //   ['A', 'B', 'C', 'D'],
  //   ['A', 'B', 'C', 'D'],
  // ]);
  const [numberLetters, setNumberLetters] =
    useState<NumberLetterObj[]>(createObjectsArray());
  const [pincode, setPincode] = useState(
    getRandomNumbersNoDuplicates(0, 9, 4).map((num) => num.toString())
  );
  const [values, setValues] = useState<string[]>(INITIAL_PINCODE_VALUES);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [rowValues, setRowValues] = useState<Record<string, string[]>>(
    createInitialRows(ROW_LENGTH, COLUMN_WIDTH + 1)
  );
  const [currentRowIndex, setCurrentRowIndex] = useState(0);
  // const [currentRowIndex, setCurrentRowIndex] = useState(5);

  const [pendingValue, setPendingValue] = useState<string | null>(null); // Temporary state for the value to update rows
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [playerWon, setPlayerWon] = useState<boolean>(false); //10 in inspector

  const handleKeyboardClick = (value: string) => {
    setValues((prev) => {
      const updatedValues = [...prev];

      if (values[currentIndex].length === 1) {
        if (currentIndex < COLUMN_WIDTH) {
          updatedValues[currentIndex + 1] = value;
        }
      } else {
        updatedValues[currentIndex] = value;
      }

      return updatedValues;
    });

    setPendingValue(value);

    if (currentIndex < COLUMN_WIDTH && values[currentIndex].length === 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleDeleteClick = () => {
    if (currentIndex >= 0 && currentIndex <= COLUMN_WIDTH) {
      setValues((prev) => {
        const updateValues = [...prev];
        updateValues[currentIndex] = '';
        return updateValues;
      });
      if (currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      }

      deleteRows();
    }
  };

  const handleEnterClick = () => {
    if (
      currentRowIndex < ROW_LENGTH &&
      fullRow(rowValues[currentRowIndex.toString()])
    ) {
      checkHasWon(rowValues[currentRowIndex.toString()]);
      checkGameOver(currentRowIndex);
      setGuesses((prev) => {
        const updatedArray = [
          ...prev,
          [...rowValues[currentRowIndex.toString()]],
        ];

        console.log('ADDING TO LOCAL STORAGE');
        addArrayToArrayInLocalStorage(PINCODE_GUESSES, [
          ...rowValues[currentRowIndex.toString()],
        ]);
        return updatedArray;
      });

      // Add guess to local storage in case user refreshes page

      setValues([...INITIAL_PINCODE_VALUES]);
      setCurrentIndex(0);
      setCurrentRowIndex((prev) => {
        checkRows();
        if (prev > ROW_LENGTH - 1) {
          return prev;
        } else {
          return prev + 1;
        }
      });
    }
  };

  const updateRows = (value: string) => {
    if (
      currentIndex <= COLUMN_WIDTH &&
      currentRowIndex < ROW_LENGTH &&
      rowValues[currentRowIndex][currentIndex].length === 1
    ) {
    } else {
      setRowValues((prev) => {
        const updateRowValues = { ...prev }; // Create a shallow copy of the previous state
        updateRowValues[currentRowIndex] = [...prev[currentRowIndex]]; // Shallow copy of the row at currentRowIndex
        updateRowValues[currentRowIndex][currentIndex] =
          getLetterValue(value, numberLetters) || ''; // Update the specific value
        return updateRowValues;
      });
    }
  };

  const checkRows = () => {
    const vals = rowValues[currentRowIndex.toString()];
    const rowValuesToPincode: string[] = [];

    vals.forEach((val) => {
      rowValuesToPincode.push(
        numberLetters.find((el) => el.letter === val)?.number || ''
      );
    });

    console.log('pincode::: ', pincode);
  };

  const deleteRows = () => {
    setRowValues((prev) => {
      const updateRowValues = { ...prev }; // Create a shallow copy of the previous state
      updateRowValues[currentRowIndex] = [...prev[currentRowIndex]]; // Shallow copy of the row at currentRowIndex
      updateRowValues[currentRowIndex][currentIndex] = ''; // Update the specific value
      return updateRowValues;
    });
  };

  const updateKeyboardCharIndicator = (value: string, type: number) => {
    const numberLettersTmp = numberLetters;
    const obj = numberLettersTmp.find((val) => val.number === value);
    if (obj) {
      obj.correctPosition = type;
      setNumberLetters((prev) =>
        prev.map((item) =>
          item.number === value ? { ...item, correctPosition: type } : item
        )
      );
    }
  };

  const getNumberLettersObj = (number: string) => {
    return (
      numberLetters.find((el) => el.number === number) || DEFAULT_NUMBER_LETTER
    );
  };

  useEffect(() => {
    if (pendingValue !== null) {
      updateRows(pendingValue);
      setPendingValue(null); // Reset pending value after updating rows
    }
  }, [pendingValue]);

  const [lastKey, setLastKey] = useState(''); // Track the last key pressed

  const handleKeyUp = useCallback(
    (event) => {
      if (isPlaying) {
        // console.log(`Key pressed: ${event.key}`);
        setLastKey(event.key); // Update state with the pressed key
        switch (event.key) {
          case 'ArrowUp':
            console.log('Up Arrow Pressed');
            break;
          case 'Backspace':
            console.log('Backspace Pressed');
            handleDeleteClick();
            break;
          case 'Enter':
            console.log('Enter Key Pressed');
            handleEnterClick();
            break;
          default:
            if (!isNaN(event.key) && event.key.trim() !== '')
              handleKeyboardClick(event.key.toString());
        }
      }
    },
    [currentIndex, handleKeyboardClick]
  );

  const checkHasWon = (values: any) => {
    const guessValues = values.map((val) => getNumberValue(val, numberLetters));
    if (areArraysEqual(guessValues, pincode)) {
      setPlayerWon(true);
      setIsPlaying(false);
    } else {
      setPlayerWon(false);
    }
  };

  const checkGameOver = (value: number) => {
    console.log('value, ROW_LENGTH::: ', value, ROW_LENGTH);
    if (value >= ROW_LENGTH - 1) {
      setIsPlaying(false);
    }
  };

  //How to Play
  const [isHowToPlayModalOpen, setIsHowToPlayModalOpen] = useState(false);
  const openHowToPlayModalHandler = () => {
    setIsHowToPlayModalOpen((prev) => !prev);
  };

  useEffect(() => {
    // Add the event listener
    window.addEventListener('keyup', handleKeyUp);

    // Cleanup to avoid memory leaks
    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyUp]);

  return (
    <Box
      sx={(theme) => ({
        overflowX: 'hidden',
        width: '100%',
        fontFamily: 'Graduate, sans-serif !important',
        '& > *': {
          fontFamily: 'inherit',
        },
        color: theme.palette.text.primary,
      })}
    >
      <Grid container direction="column">
        <Grid item xs={6} sx={{ textAlign: 'center', pt: '0px !important' }}>
          <GameHeader openHowToPlayModalHandler={openHowToPlayModalHandler} />
          {/* Pincode: {pincode} */}
        </Grid>
      </Grid>
      <Box
        sx={{
          margin: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          pb: 2,
        }}
        justifyContent="flex-end"
      >
        <Stack
          direction="column"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          alignContent="flex-end"
          sx={(theme) => ({
            margin: 'auto',
            height: '85vh',
            width: 335,
            // border: '1px solid red',
          })}
        >
          <Stack
            direction="column"
            spacing={2}
            justifyContent="flex-end"
            alignItems="flex-end"
            alignContent="flex-end"
            sx={(theme) => ({
              width: '100%',
              height: '100%',
            })}
          >
            {guesses.map((guess: string[], index: number) => (
              <LetterRow
                key={index}
                values={guess}
                isHighlighted={index === currentRowIndex ? true : false}
                pincode={pincode}
                currentRowIndex={currentRowIndex}
                rowIndex={index}
                updateKeyboardCharIndicator={updateKeyboardCharIndicator}
                numberLetters={numberLetters}
              />
            ))}
          </Stack>

          <Stack
            direction="column"
            spacing={1}
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-end"
            sx={(theme) => ({ width: '100%' })}
          >
            {!playerWon && isPlaying && (
              <Box sx={(theme) => ({ py: 1, width: '100%' })}>
                <PinCodeGuess values={values} />
              </Box>
            )}
            {!playerWon && !isPlaying && (
              <Box sx={(theme) => ({ py: 1, width: '100%' })}>
                {/* This is the correct pincode */}
                <PinCodeGuess values={pincode} />
              </Box>
            )}
            {!playerWon && isPlaying && (
              <Stack direction="column" spacing={1}>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <KeyboardChar
                    numberLetter={getNumberLettersObj('0')}
                    onClick={handleKeyboardClick}
                  />
                  <KeyboardChar
                    numberLetter={getNumberLettersObj('1')}
                    onClick={handleKeyboardClick}
                  />
                  <KeyboardChar
                    numberLetter={getNumberLettersObj('2')}
                    onClick={handleKeyboardClick}
                  />
                  <KeyboardChar
                    numberLetter={getNumberLettersObj('3')}
                    onClick={handleKeyboardClick}
                  />
                  <KeyboardChar
                    numberLetter={getNumberLettersObj('4')}
                    onClick={handleKeyboardClick}
                  />
                  <KeyboardChar
                    numberLetter={getNumberLettersObj('5')}
                    onClick={handleKeyboardClick}
                  />
                </Stack>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <KeyboardChar
                    numberLetter={getNumberLettersObj('6')}
                    onClick={handleKeyboardClick}
                  />
                  <KeyboardChar
                    numberLetter={getNumberLettersObj('7')}
                    onClick={handleKeyboardClick}
                  />
                  <KeyboardChar
                    numberLetter={getNumberLettersObj('8')}
                    onClick={handleKeyboardClick}
                  />
                  <KeyboardChar
                    numberLetter={getNumberLettersObj('9')}
                    onClick={handleKeyboardClick}
                  />
                  <KeyboardChar
                    numberLetter={{
                      correctPosition: 0,
                      letter: '-',
                      number: 'Del',
                    }}
                    onClick={handleDeleteClick}
                  />
                  <KeyboardChar
                    numberLetter={{
                      correctPosition: 0,
                      letter: '-',
                      number: 'Ent',
                    }}
                    onClick={handleEnterClick}
                  />
                </Stack>
              </Stack>
            )}
            {playerWon && !isPlaying && (
              <Stack
                spacing={2}
                sx={(theme) => ({ py: 4, width: '100%', textAlign: 'center' })}
              >
                <Typography variant="h5">Congratulations!</Typography>
                <PinCodeGuess
                  values={guesses[guesses.length - 1].map((el) =>
                    getNumberValue(el, numberLetters)
                  )}
                />
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>
      <HowToPlayModal
        isOpen={isHowToPlayModalOpen}
        setIsHowToPlayModalOpen={setIsHowToPlayModalOpen}
        openHowToPlayModalHandler={openHowToPlayModalHandler}
      >
        <HowToPlayModalChildren highscore={100} />
      </HowToPlayModal>
    </Box>
  );
};

interface HowToPlayModalChildrenProps {
  highscore: number; // Adjust this type based on the actual data type of highscore
}

const HowToPlayModalChildren: FC<HowToPlayModalChildrenProps> = ({
  highscore,
}) => (
  <Grid item>
    <Typography variant="h5" sx={{ fontSize: '24px', fontWeight: '700' }}>
      How To Play
    </Typography>
    <br />
    <Typography variant="body1" sx={{ fontSize: '16px' }}>
      Each number is assigned a letter
    </Typography>
    <br />
    <Typography variant="body1" sx={{ fontSize: '16px' }}>
      You have 5 guesses to find the pincode
    </Typography>
    <br />
    <Typography variant="h6">Good luck!</Typography>
  </Grid>
);

const getLetterValue = (value: string, numberLetters: any) => {
  return numberLetters.find((nl) => nl.number === value)?.letter;
};

const getNumberValue = (value: string, numberLetters: any) => {
  return numberLetters.find((nl) => nl.letter === value)?.number;
};

const fullRow = (values: string[]) => {
  return values.every((element) => element !== '');
};

const createInitialRows = (
  rows: number,
  columns: number
): Record<string, string[]> => {
  const initialRows: Record<string, string[]> = {};

  for (let i = 0; i < rows; i++) {
    initialRows[i.toString()] = Array(columns).fill('');
  }

  return initialRows;
};

const createObjectsArray = (): NumberLetterObj[] => {
  const numbers = Array.from({ length: 10 }, (_, i) => i); // Explicitly create an array
  const letters = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(65 + i)
  ); // Create array ['A', 'B', ..., 'Z']

  // Helper function to shuffle an array
  const shuffleArray = (arr: any[]) => arr.sort(() => Math.random() - 0.5);

  const shuffledNumbers = shuffleArray(numbers); // Shuffle numbers
  const shuffledLetters = shuffleArray(letters).slice(0, 10); // Shuffle letters and pick 10 unique ones

  // Create the array of objects
  const result = shuffledNumbers.map(
    (number: number, index: number) =>
      ({
        correctPosition: 0,
        letter: shuffledLetters[index],
        number: number.toString(),
      }) as NumberLetterObj
  );

  return result;
};

export default PinCodeGame;
